import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  Grid,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Icon,
  useColorModeValue,
  Stack,
  VStack,
} from '@chakra-ui/react';
import { FaMoneyBillWave, FaCalendarAlt, FaClock } from 'react-icons/fa';
import axios from 'axios';

const Earnings = () => {
  const [earnings, setEarnings] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('all');
  const [statistics, setStatistics] = useState({
    totalEarnings: 0,
    monthlyEarnings: 0,
    weeklyEarnings: 0,
    monthlyGrowth: 0,
    weeklyGrowth: 0
  });

  // Color mode values
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const tableHeaderBg = useColorModeValue('gray.50', 'gray.700');
  const tableHoverBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    fetchEarnings();
  }, []);

  const fetchEarnings = async () => {
    const ticketToken = sessionStorage.getItem('ticketToken');
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/applicant/completed-earnings`,
        {
          headers: {
            Authorization: `Bearer ${ticketToken}`
          }
        }
      );
      
      const transformedData = response.data.data;
      setEarnings(transformedData);
      calculateStatistics(transformedData);
    } catch (error) {
      console.error('Error fetching earnings:', error);
      setEarnings([]);
    }
  };

  const calculateStatistics = (data) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const lastMonth = new Date(now.setMonth(now.getMonth() - 1)).getMonth();
    
    const currentMonthEarnings = data
      .filter(job => new Date(job.completedDate).getMonth() === currentMonth)
      .reduce((sum, job) => sum + (job.totalAmountToBePayable || 0), 0);

    const lastMonthEarnings = data
      .filter(job => new Date(job.completedDate).getMonth() === lastMonth)
      .reduce((sum, job) => sum + (job.totalAmountToBePayable || 0), 0);

    const monthlyGrowth = lastMonthEarnings ? 
      ((currentMonthEarnings - lastMonthEarnings) / lastMonthEarnings) * 100 : 0;

    setStatistics({
      totalEarnings: data.reduce((sum, job) => sum + (job.totalAmountToBePayable || 0), 0),
      monthlyEarnings: currentMonthEarnings,
      weeklyEarnings: calculateWeeklyEarnings(data),
      monthlyGrowth,
      weeklyGrowth: calculateWeeklyGrowth(data)
    });
  };

  const calculateWeeklyEarnings = (data) => {
    const weekStart = new Date();
    weekStart.setDate(weekStart.getDate() - weekStart.getDay());
    return data
      .filter(job => new Date(job.completedDate) >= weekStart)
      .reduce((sum, job) => sum + job.totalAmountToBePayable, 0);
  };

  const calculateWeeklyGrowth = (data) => {
    return 15; // Placeholder
  };

  const filterEarnings = () => {
    if (selectedPeriod === 'all') return earnings;
    
    const currentDate = new Date();
    const filterDate = new Date();
    
    switch (selectedPeriod) {
      case 'week':
        filterDate.setDate(currentDate.getDate() - 7);
        break;
      case 'month':
        filterDate.setMonth(currentDate.getMonth() - 1);
        break;
      case 'year':
        filterDate.setFullYear(currentDate.getFullYear() - 1);
        break;
      default:
        return earnings;
    }
    
    return earnings.filter(job => new Date(job.completedDate) >= filterDate);
  };

  return (
    <Box p={{ base: 2, md: 6 }}>
      {/* Stats Cards - Responsive Grid */}
      <Grid 
        templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} 
        gap={{ base: 4, md: 6 }} 
        mb={{ base: 6, md: 8 }}
      >
        {/* Total Earnings Card */}
        <Card bg={bgColor} boxShadow="lg" borderRadius="lg">
          <CardBody>
            <Stack direction={{ base: 'row', md: 'row' }} align="center" spacing={4}>
              <Icon as={FaMoneyBillWave} boxSize={{ base: 8, md: 10 }} color="green.500" />
              <Stat>
                <StatLabel fontSize={{ base: 'md', md: 'lg' }}>Total Earnings</StatLabel>
                <StatNumber fontSize={{ base: 'xl', md: '2xl' }}>£{statistics.totalEarnings.toFixed(2)}</StatNumber>
                <StatHelpText fontSize={{ base: 'sm', md: 'md' }}>
                  <StatArrow type={statistics.monthlyGrowth >= 0 ? 'increase' : 'decrease'} />
                  {Math.abs(statistics.monthlyGrowth).toFixed(1)}% from last month
                </StatHelpText>
              </Stat>
            </Stack>
          </CardBody>
        </Card>

        {/* Monthly Earnings Card */}
        <Card bg={bgColor} boxShadow="lg" borderRadius="lg">
          <CardBody>
            <Stack direction={{ base: 'row', md: 'row' }} align="center" spacing={4}>
              <Icon as={FaCalendarAlt} boxSize={{ base: 8, md: 10 }} color="blue.500" />
              <Stat>
                <StatLabel fontSize={{ base: 'md', md: 'lg' }}>Monthly Earnings</StatLabel>
                <StatNumber fontSize={{ base: 'xl', md: '2xl' }}>£{statistics.monthlyEarnings.toFixed(2)}</StatNumber>
                <StatHelpText fontSize={{ base: 'sm', md: 'md' }}>Current month</StatHelpText>
              </Stat>
            </Stack>
          </CardBody>
        </Card>

        {/* Weekly Earnings Card */}
        <Card bg={bgColor} boxShadow="lg" borderRadius="lg">
          <CardBody>
            <Stack direction={{ base: 'row', md: 'row' }} align="center" spacing={4}>
              <Icon as={FaClock} boxSize={{ base: 8, md: 10 }} color="purple.500" />
              <Stat>
                <StatLabel fontSize={{ base: 'md', md: 'lg' }}>Weekly Earnings</StatLabel>
                <StatNumber fontSize={{ base: 'xl', md: '2xl' }}>£{statistics.weeklyEarnings.toFixed(2)}</StatNumber>
                <StatHelpText fontSize={{ base: 'sm', md: 'md' }}>
                  <StatArrow type={statistics.weeklyGrowth >= 0 ? 'increase' : 'decrease'} />
                  {Math.abs(statistics.weeklyGrowth).toFixed(1)}% from last week
                </StatHelpText>
              </Stat>
            </Stack>
          </CardBody>
        </Card>
      </Grid>

      {/* Filter Section */}
      <Flex 
        direction={{ base: 'column', md: 'row' }} 
        justify="space-between" 
        align={{ base: 'start', md: 'center' }} 
        mb={6} 
        gap={4}
      >
        <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="bold">Earnings History</Text>
        <Select
          width={{ base: 'full', md: '200px' }}
          value={selectedPeriod}
          onChange={(e) => setSelectedPeriod(e.target.value)}
          bg={bgColor}
          borderColor={borderColor}
        >
          <option value="all">All Time</option>
          <option value="week">Last Week</option>
          <option value="month">Last Month</option>
          <option value="year">Last Year</option>
        </Select>
      </Flex>

      {/* Responsive Table */}
      <Box overflowX="auto" borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
        <Table variant="simple" size={{ base: 'sm', md: 'md' }}>
          <Thead bg={tableHeaderBg}>
            <Tr>
              <Th>Job Title</Th>
              <Th display={{ base: 'none', md: 'table-cell' }}>Date</Th>
              <Th>Hours</Th>
              <Th>Rate</Th>
              <Th isNumeric>Amount</Th>
            </Tr>
          </Thead>
          <Tbody>
            {earnings.map((job) => (
              <Tr key={job._id} _hover={{ bg: tableHoverBg }}>
                <Td fontWeight="medium" fontSize={{ base: 'sm', md: 'md' }}>
                  <VStack align="start" spacing={1}>
                    <Text>{job.jobTitle}</Text>
                    <Text display={{ base: 'block', md: 'none' }} fontSize="xs" color="gray.500">
                      {new Date(job.completedDate).toLocaleDateString()}
                    </Text>
                  </VStack>
                </Td>
                <Td display={{ base: 'none', md: 'table-cell' }}>
                  {new Date(job.completedDate).toLocaleDateString()}
                </Td>
                <Td fontSize={{ base: 'sm', md: 'md' }}>{job.totalWorkingHours || 0}</Td>
                <Td fontSize={{ base: 'sm', md: 'md' }}>£{job.payrate || 0}</Td>
                <Td isNumeric fontWeight="bold" color="green" fontSize={{ base: 'sm', md: 'md' }}>
                  £{(job.totalAmountToBePayable || 0).toFixed(1)}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default Earnings; 